import { FaTrophy, FaGlobe, FaUsers, FaShieldAlt, FaAward, FaMedal, FaStar, FaHandshake, FaTruck, FaCheck } from 'react-icons/fa'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { PageHeader, Card } from '../components/common'
import { siteConfig } from '../config/site'

const milestones = [
  { year: '2015', title: '公司成立', description: '在深圳成立，开始国际物流业务' },
  { year: '2017', title: '业务扩展', description: '业务覆盖欧美主要国家，客户突破1万家' },
  { year: '2019', title: '行业认证', description: '获得国际货运代理资质，物流信息化升级' },
  { year: '2021', title: '数字化转型', description: '推出智能物流平台，实现全程可视化追踪' },
  { year: '2023', title: '行业领先', description: '服务客户超过5万家，覆盖200+国家和地区' },
]

const achievements = [
  {
    icon: FaTrophy,
    title: '行业十强企业',
    description: '连续三年入选中国跨境物流行业十强企业',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
  },
  {
    icon: FaAward,
    title: '诚信经营示范单位',
    description: '广东省物流行业协会授予诚信经营示范单位',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
  },
  {
    icon: FaMedal,
    title: '优质服务奖',
    description: '荣获2023年度跨境电商物流优质服务奖',
    color: 'text-green-500',
    bgColor: 'bg-green-50',
  },
  {
    icon: FaStar,
    title: '客户满意度金奖',
    description: '客户满意度评分持续保持行业领先水平',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
  },
]

const advantages = [
  {
    icon: FaGlobe,
    title: '全球网络覆盖',
    description: '自有仓储网络遍布全球，覆盖200+国家和地区，让您的货物无论发往何处都能安全送达。',
  },
  {
    icon: FaUsers,
    title: '专业团队服务',
    description: '拥有超过500名物流专业人员，提供7x24小时全天候服务，专业客服一对一跟进。',
  },
  {
    icon: FaShieldAlt,
    title: '安全有保障',
    description: '货物全程保险保障，完善的补偿机制，让您的每一笔运输都安心无忧。',
  },
  {
    icon: FaHandshake,
    title: '价格透明实惠',
    description: '标准化报价体系，无隐藏费用，同等服务下价格更具竞争力。',
  },
]

const stats = [
  { number: '200+', label: '覆盖国家/地区' },
  { number: '50K+', label: '累计服务客户' },
  { number: '99.5%', label: '准时送达率' },
  { number: '8+', label: '行业经验(年)' },
]

export default function AboutPage() {
  const breadcrumb = useBreadcrumb()
  return (
    <div>
      <PageHeader
        icon={FaTruck}
        title="关于我们"
        subtitle="专业跨境物流服务商，值得信赖的合作伙伴"
        breadcrumb={breadcrumb}
      />

      <div className="container py-12">
        {/* 公司简介 */}
        <section className="mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-fluid-3xl font-bold text-gray-900 mb-6">公司简介</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                {siteConfig.companyName || '物流集货平台'}是一家专注于跨境物流服务的综合性物流企业，
                成立于2015年，总部位于中国深圳。公司致力于为全球客户提供高效、安全、透明的一站式物流解决方案。
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                我们整合了空运、海运、快递等多种运输渠道，建立了覆盖全球200多个国家和地区的服务网络。
                凭借先进的物流信息化系统，客户可以实时追踪货物动态，实现全程可视化管理。
              </p>
              <p className="text-gray-600 leading-relaxed">
                多年来，我们始终秉承「使命必达，客户至上」的服务理念，已累计为超过50000家企业和个人客户
                提供了优质的物流服务，赢得了广大客户的信赖和好评。
              </p>
            </div>
            <div className="grid grid-cols-fluid-sm gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl text-center border border-primary-100">
                  <div className="text-fluid-4xl font-bold text-primary-600 mb-2">{stat.number}</div>
                  <div className="text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 核心优势 */}
        <section className="mb-16">
          <h2 className="text-fluid-3xl font-bold text-gray-900 text-center mb-4">核心优势</h2>
          <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
            凭借多年行业经验和持续创新，我们为客户提供更优质的服务体验
          </p>
          <div className="grid grid-cols-fluid-lg gap-6">
            {advantages.map((item, index) => {
              const Icon = item.icon
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                  <Card.Body>
                    <div className="w-14 h-14 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center mx-auto mb-4">
                      <Icon className="text-2xl" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                  </Card.Body>
                </Card>
              )
            })}
          </div>
        </section>

        {/* 荣誉资质 */}
        <section className="mb-16 bg-gray-50 -mx-4 px-4 py-16 lg:-mx-8 lg:px-8">
          <h2 className="text-fluid-3xl font-bold text-gray-900 text-center mb-4">荣誉资质</h2>
          <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
            专业能力获得行业认可，用心服务赢得客户信赖
          </p>
          <div className="grid grid-cols-fluid-lg gap-6 max-w-6xl mx-auto">
            {achievements.map((item, index) => {
              const Icon = item.icon
              return (
                <div key={index} className="bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <div className={`w-16 h-16 rounded-full ${item.bgColor} ${item.color} flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="text-fluid-3xl" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-500 text-sm">{item.description}</p>
                </div>
              )
            })}
          </div>
        </section>

        {/* 发展历程 */}
        <section className="mb-16">
          <h2 className="text-fluid-3xl font-bold text-gray-900 text-center mb-4">发展历程</h2>
          <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
            砥砺前行，稳步成长，不断突破
          </p>
          <div className="relative max-w-4xl mx-auto">
            {/* Timeline Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 -translate-x-1/2 hidden md:block" />
            
            {milestones.map((milestone, index) => (
              <div key={index} className={`relative flex items-center mb-8 md:mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                {/* Content */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:text-left'}`}>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <span className="inline-block px-3 py-1 bg-primary-100 text-primary-600 rounded-full text-sm font-semibold mb-3">
                      {milestone.year}
                    </span>
                    <h4 className="font-semibold text-gray-900 mb-2">{milestone.title}</h4>
                    <p className="text-gray-500 text-sm">{milestone.description}</p>
                  </div>
                </div>
                
                {/* Dot */}
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary-500 border-4 border-white shadow" />
                
                {/* Spacer */}
                <div className="hidden md:block flex-1" />
              </div>
            ))}
          </div>
        </section>

        {/* 服务承诺 */}
        <section className="bg-gradient-to-br from-primary-700 to-primary-900 text-white rounded-2xl p-8 lg:p-12">
          <h2 className="text-fluid-3xl font-bold text-center mb-4">我们的承诺</h2>
          <p className="text-white/80 text-center mb-10 max-w-2xl mx-auto">
            以客户为中心，用专业创造价值
          </p>
          <div className="grid grid-cols-fluid-lg gap-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <FaCheck className="text-xl" />
              </div>
              <h4 className="font-semibold mb-2">准时送达</h4>
              <p className="text-white/70 text-sm">严格时效管理，99.5%准时送达率</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <FaCheck className="text-xl" />
              </div>
              <h4 className="font-semibold mb-2">安全保障</h4>
              <p className="text-white/70 text-sm">全程保险，货物丢损全额赔付</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <FaCheck className="text-xl" />
              </div>
              <h4 className="font-semibold mb-2">透明服务</h4>
              <p className="text-white/70 text-sm">价格透明，全程可追踪</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
